import {
  ISalesCountQuotesCount,
  QuoteStatusEnum,
  SalesCreatedByFilter,
} from '@monetize/types/app';
import { FunctionComponent as FC, useMemo } from 'react';
import { Link as ReactDomLink } from 'react-router-dom';
import { useGetDashboardSalesCountQuotes } from '../../../../../api/dashboardsService';
import {
  MBox,
  MChart,
  MFlex,
  MLink,
  MSkeleton,
  MText,
} from '../../../../../components/Monetize';
import { ROUTES } from '../../../../../constants';
import { QuoteStatusEnumDisplay } from '../../../../../constants/quotes';
import { BASE_RADICAL_CHART_OPTIONS } from '../../../../../utils/charts';
import { AccessDeniedSmall } from '../../../../AccessDeniedSmall';
import './styles.scss';

export const QuoteStatusEnumDisplayCustom: {
  [key in QuoteStatusEnum]: string;
} = {
  ...QuoteStatusEnumDisplay,
  REVIEW: 'In Review',
};

const QuotesChart: FC<{
  createdByFilter: SalesCreatedByFilter;
  currency: string;
}> = ({ createdByFilter, currency }) => {
  const { isLoading, data, error } = useGetDashboardSalesCountQuotes(
    {
      currency,
      query: createdByFilter,
    },
    {
      select: (respData) => {
        // Select data for only these statuses
        const allowedQuoteStatus = [
          QuoteStatusEnum.REVIEW,
          QuoteStatusEnum.APPROVED,
          QuoteStatusEnum.SENT,
        ];
        const quoteCounts: ISalesCountQuotesCount[] = allowedQuoteStatus.map(
          (status) => {
            const quoteS = respData.quoteCounts.find(
              (qc) => qc.status === status,
            );
            return { status: status as string, count: quoteS?.count || 0 };
          },
        ) as ISalesCountQuotesCount[];
        return {
          total: quoteCounts.reduce((acc, qc) => acc + qc.count, 0),
          quoteCounts,
        };
      },
    },
  );

  // react-apexcharts compares `options` by value but functions by reference, so a new
  // formatter closure on every render forces a full chart updateOptions() round-trip.
  const chartOptions = useMemo(
    () => ({
      ...BASE_RADICAL_CHART_OPTIONS,
      legend: {
        ...BASE_RADICAL_CHART_OPTIONS.legend,
        formatter(seriesName: string, opts: any) {
          return (
            QuoteStatusEnumDisplayCustom[seriesName as QuoteStatusEnum] +
            ':  ' +
            data?.quoteCounts[opts.seriesIndex].count
          );
        },
      },
      labels: data?.quoteCounts.map(({ status }) => status),
    }),
    [data],
  );

  const series = useMemo(
    () =>
      data?.quoteCounts.map(({ count }) =>
        data.total > 0 ? (count / data.total) * 100 : 0,
      ),
    [data],
  );

  return (
    <MBox h="400px">
      <MFlex alignItems="center">
        <MText fontWeight="bold" fontSize="2xl">
          Quotes
        </MText>
        <MLink fontSize="sm" asChild cursor="pointer" ml="3" mt="1">
          <ReactDomLink to={ROUTES.SALES_QUOTES_ROUTE}>
            View Quotes
          </ReactDomLink>
        </MLink>
      </MFlex>

      <MFlex justify="center">
        <MBox w="400px">
          {isLoading && <MSkeleton w="100%" h="20px" />}
          {!error && !isLoading && (
            <MChart
              options={chartOptions}
              series={series}
              type="radialBar"
              height="350px"
              className="sales-quotes-chart"
            />
          )}
          {!isLoading && !!error && (
            <AccessDeniedSmall containerProps={{ mt: '4' }} />
          )}
        </MBox>
      </MFlex>
    </MBox>
  );
};

export { QuotesChart };
